var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import clsx from 'clsx';
import { isNil, useClasses, useConsoleLog, useLayoutProps, useProps, } from '@hawkins/helpers';
import { Button } from '../button';
import { TOGGLE_BUTTON_GROUP_MENU_TRIGGER_ATTR } from '../toggleButtonGroup/toggleButtonGroup.constants';
import { ToggleButtonGroupContext } from '../toggleButtonGroup/toggleButtonGroup.context';
import { useToggleButtonStyles } from './toggleButton.styles';
var displayName = 'ToggleButton';
/**
 * Toggle buttons can be used to switch between multiple states.
 */
export var ToggleButton = React.forwardRef(function (_a, ref) {
    var _b, _c;
    var children = _a.children, props = __rest(_a, ["children"]);
    // Props
    var _d = useProps(displayName, props), classesProp = _d.classes, className = _d.className, _e = _d.compact, compactProp = _e === void 0 ? false : _e, _f = _d.disabled, disabledProp = _f === void 0 ? false : _f, iconPropsProp = _d.iconProps, layout = _d.layout, _g = _d.noWrap, noWrap = _g === void 0 ? true : _g, onChangeProp = _d.onChange, styleProp = _d.style, value = _d.value, _h = _d.variant, variant = _h === void 0 ? 'tertiary' : _h, restProps = __rest(_d, ["classes", "className", "compact", "disabled", "iconProps", "layout", "noWrap", "onChange", "style", "value", "variant"]);
    var isOverflowMenuTrigger = !!restProps[TOGGLE_BUTTON_GROUP_MENU_TRIGGER_ATTR];
    var hasValue = !isNil(value);
    // Context
    var groupContext = React.useContext(ToggleButtonGroupContext);
    var compact = (groupContext === null || groupContext === void 0 ? void 0 : groupContext.compact) || compactProp;
    var contextOnChange = groupContext === null || groupContext === void 0 ? void 0 : groupContext.onChange;
    var contextValue = groupContext === null || groupContext === void 0 ? void 0 : groupContext.value;
    var disabled = ((groupContext === null || groupContext === void 0 ? void 0 : groupContext.disabled) || disabledProp) && !isOverflowMenuTrigger;
    var exclusive = (groupContext === null || groupContext === void 0 ? void 0 : groupContext.exclusive) || false;
    var isInGroup = !!groupContext;
    var tabbableValue = groupContext === null || groupContext === void 0 ? void 0 : groupContext.tabbableValue;
    var selected = isInGroup && hasValue && (Array.isArray(contextValue) ? contextValue.includes(value) : contextValue === value);
    useConsoleLog(isInGroup && !hasValue, 'A `ToggleButton` inside a `ToggleButtonGroup` is missing `props.value`. This is required for the `ToggleButtonGroup` to work properly.', { displayName: displayName, docsPath: 'components-toggle-button#usage', level: 'error' });
    // Callbacks
    var onClick = React.useCallback(function (ev) {
        if (isOverflowMenuTrigger)
            return;
        onChangeProp === null || onChangeProp === void 0 ? void 0 : onChangeProp(ev, value, !selected);
        contextOnChange === null || contextOnChange === void 0 ? void 0 : contextOnChange(ev, value);
    }, [contextOnChange, isOverflowMenuTrigger, onChangeProp, selected, value]);
    // Styles
    var baseClasses = useToggleButtonStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var style = useLayoutProps({ layout: layout, style: styleProp });
    var baseButtonClassName = clsx(classes.HAWKINS_BASE, className, (_b = {},
        // HACK: `ToggleButton` allows for displaying an icon inside a compact button, but `Button` does NOT,
        // so implement compact sizing within `ToggleButton` rather than relying on the implementation in `Button`.
        //
        // TODO(icon): Rely on the compact implementation in `Button` if the design team
        // ever decides to allow `Button` to display icons inside compact buttons.
        _b[classes.rootCompact] = compact,
        _b[classes.rootEnabled] = !disabled,
        _b[classes.rootSelected] = selected,
        _b));
    var rootClassName = clsx(baseButtonClassName, classes.root);
    // Content
    var iconProps = React.useMemo(function () {
        var baseIconProps = {};
        if (compact)
            baseIconProps.size = 'small';
        return __assign(__assign({}, baseIconProps), iconPropsProp);
    }, [compact, iconPropsProp]);
    var buttonProps = (_c = {},
        _c[exclusive ? 'aria-checked' : 'aria-pressed'] = !!selected,
        _c.ariaDisableOnly = true,
        _c.disabled = disabled,
        _c.iconProps = iconProps,
        _c.noWrap = noWrap,
        _c.onClick = onClick,
        _c.ref = ref,
        _c.style = style,
        // When inside a `ToggleButtonGroup`, the first selected toggle button should be tabbable.
        // If no toggle button is selected, then the first toggle button should be tabbable.
        //
        // When NOT inside a `ToggleButtonGroup` or if there is no `tabbableValue` set for some reason,
        // make all toggle buttons tabbable.
        _c.tabIndex = tabbableValue === undefined || tabbableValue === value ? 0 : -1,
        _c.variant = variant,
        _c);
    if (exclusive)
        buttonProps.role = 'radio';
    return (_jsx(Button, __assign({ className: rootClassName }, buttonProps, restProps, { children: children })));
});
ToggleButton.displayName = displayName;
